import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import CustomTable from '@/shared/components/ui/Button/CustomTable'
import TextButton from '@/shared/components/ui/Button/TextButton'
import QueryBar from './results/QueryBar'
import NoResultsState from './results/NoResultsState'
import AccountDetailsDrawer from './results/AccountDetailsDrawer'
import AddToAccountsButton from './results/AddToAccountsButton'
import { renderResultCell } from './results/ResultCells'
import { RESULT_COLUMNS, SKELETON_ROWS, TABLE_PROPS } from './results/config'
import { getAccountKey } from './results/utils'
import ResultsCount from '@/shared/components/ui/Status/ResultsCount'

const CustomTableComponent = CustomTable as React.ComponentType<any>
const TextButtonComponent = TextButton as React.ComponentType<any>

interface CustomSearchResultsTableProps {
    queryName?: string
    queryText?: string
    onChangeQueryText?: (text: string) => void
    onSearch?: (query: string) => void
    customQueries?: any[]
    selectedCustomQueryId?: string | number
    onSelectQuery?: (query: any) => void
    onRunQuery?: (query?: any) => void
    onNewSearch?: () => void
    isLoading?: boolean
    accounts?: any[]
    onAddAccount?: (account: any) => void
}

export default function CustomSearchResultsTable({
    queryName,
    queryText,
    onChangeQueryText,
    onSearch,
    customQueries = [],
    selectedCustomQueryId,
    onSelectQuery,
    onRunQuery,
    onNewSearch,
    isLoading = false,
    accounts = [],
}: CustomSearchResultsTableProps) {
    const [selectedAccount, setSelectedAccount] = useState<any>(null)
    const displayAccounts = useMemo(() => (Array.isArray(accounts) ? accounts : []), [accounts])
    const hasResults = displayAccounts.length > 0
    // A re-run ("Find more") keeps the current rows on screen; skeletons only for a first run.
    const showSkeleton = isLoading && !hasResults

    const activeQuery = useMemo(
        () =>
            customQueries.find((q) => String(q.id) === String(selectedCustomQueryId)) ||
            customQueries[0] ||
            null,
        [customQueries, selectedCustomQueryId],
    )
    const selectedId = String(activeQuery?.id ?? selectedCustomQueryId ?? '')

    const [currentQueryText, setCurrentQueryText] = useState(queryText || activeQuery?.query || '')
    useEffect(() => {
        setCurrentQueryText(queryText || activeQuery?.query || '')
    }, [queryText, activeQuery?.query])

    const handleQueryChange = (value: string) => {
        setCurrentQueryText(value)
        onChangeQueryText?.(value)
    }

    // Clicking the same account again closes the panel.
    const toggleAccount = useCallback((account: any) => {
        setSelectedAccount((current: any) =>
            current && getAccountKey(current) === getAccountKey(account) ? null : account,
        )
    }, [])

    const renderCell = useCallback(
        (account: any, columnKey: string) => renderResultCell(account, columnKey, toggleAccount),
        [toggleAccount],
    )

    return (
        <>
            {/* Fills its container (page or Find tab) so the table never runs past the screen. */}
            <div className='flex flex-col gap-4 w-full h-full px-6 pb-4'>
                <QueryBar
                    customQueries={customQueries}
                    selectedId={selectedId}
                    placeholder={queryName || activeQuery?.name || 'Custom Search'}
                    value={currentQueryText}
                    criteria={activeQuery?.criteria}
                    onChange={handleQueryChange}
                    onSearch={onSearch}
                    onSelectQuery={onSelectQuery}
                    onNewSearch={onNewSearch}
                />

                <div className='flex items-center justify-between gap-3'>
                    <ResultsCount
                        isLoading={isLoading}
                        count={displayAccounts.length}
                        loadingText={hasResults ? 'Finding more companies' : undefined}
                    />
                    {hasResults && onRunQuery && activeQuery && (
                        <TextButtonComponent
                            onClick={() => onRunQuery(activeQuery)}
                            isLoading={isLoading}
                            tooltip='Run this search again for companies not found yet'
                            className={"hover:bg-transparent"}
                        >
                            <span className='flex items-center gap-1'>
                                <RefreshCw size={12} /> Find more
                            </span>
                        </TextButtonComponent>
                    )}
                </div>

                {!isLoading && displayAccounts.length === 0 ? (
                    <NoResultsState
                        finished={activeQuery?.last_run_status === 'completed'}
                        queryText={currentQueryText}
                        criteria={activeQuery?.criteria}
                        onSearch={onSearch}
                        onRunQuery={onRunQuery ? () => onRunQuery(activeQuery) : undefined}
                        onNewSearch={onNewSearch}
                    />
                ) : (
                    <div className='flex-1 min-h-0 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden shadow-xs'>
                        <CustomTableComponent
                            data={showSkeleton ? SKELETON_ROWS : displayAccounts}
                            columns={RESULT_COLUMNS}
                            renderCell={renderCell}
                            enableSelection={!showSkeleton}
                            {...TABLE_PROPS}
                            height='100%'
                        />
                    </div>
                )}
            </div>

            <AccountDetailsDrawer
                account={selectedAccount}
                onClose={() => setSelectedAccount(null)}
                emptyOverviewText='No overview was found for this company. Run the research again to refresh it.'
                renderFooter={(account) => <AddToAccountsButton account={account} />}
            />
        </>
    )
}
